import { BaseClient } from '../client/BaseClient';
import { ApiResponse } from '../types';

export interface Chat {
  _id: string;
  id: any;
  tel: string;
  name: string;
  isGroup: boolean;
  unreadCount: number;
  timestamp: number;
  archived: boolean;
  pinned: boolean;
  profilePicUrl?: string;
  isMuted: boolean;
  lastMessage?: {
    id: any;
    body: string;
    from: string;
    timestamp: number;
    type: string;
    fromMe: boolean;
  };
  groupMetadata?: {
    id: string;
    subject: string;
    desc?: string;
    owner?: string;
    participants: Array<{
      id: string;
      admin?: 'admin' | 'superadmin' | null;
    }>;
  };
  participants?: string[];
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ChatFilters {
  tel?: string;
  groups?: boolean;
  unreadOnly?: boolean;
  archived?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ChatsResponse {
  success: boolean;
  data: {
    chats: Chat[];
    total: number;
    page: number;
    limit: number;
  };
}

export interface ChatResponse {
  success: boolean;
  data: Chat;
}

export class Chats {
  constructor(private client: BaseClient) {}

  /**
   * List all chats with optional filtering
   * @param params - Query parameters for filtering
   */
  async list(params?: ChatFilters): Promise<ChatsResponse> {
    return this.client['get']<ChatsResponse>('/api/chats', { params });
  }

  /**
   * Get a specific chat by ID
   * @param tel - Phone number
   * @param chatId - Chat ID
   */
  async get(tel: string, chatId: string): Promise<ChatResponse> {
    const response = await this.client['get']<ChatResponse>(
      `/api/chats/${encodeURIComponent(chatId)}`,
      { params: { tel } }
    );
    return response;
  }

  /**
   * Mark chat as read
   * @param tel - Phone number
   * @param chatId - Chat ID
   */
  async markAsRead(tel: string, chatId: string): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(
      `/api/chats/${encodeURIComponent(chatId)}/read`,
      { tel }
    );
  }

  /**
   * Send typing indicator to chat
   * @param tel - Phone number
   * @param chatId - Chat ID
   */
  async sendTyping(tel: string, chatId: string): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(
      `/api/chats/${encodeURIComponent(chatId)}/typing`,
      { tel }
    );
  }

  /**
   * Archive or unarchive a chat
   * @param tel - Phone number
   * @param chatId - Chat ID
   * @param archived - Archive status
   */
  async archive(tel: string, chatId: string, archived: boolean = true): Promise<{ success: boolean }> {
    return this.client['patch']<{ success: boolean }>(
      `/api/chats/${encodeURIComponent(chatId)}`,
      { tel, archived }
    );
  }

  /**
   * Pin or unpin a chat
   * @param tel - Phone number
   * @param chatId - Chat ID
   * @param pinned - Pin status
   */
  async pin(tel: string, chatId: string, pinned: boolean = true): Promise<{ success: boolean }> {
    return this.client['patch']<{ success: boolean }>(
      `/api/chats/${encodeURIComponent(chatId)}`,
      { tel, pinned }
    );
  }

  /**
   * Mute or unmute a chat
   * @param tel - Phone number
   * @param chatId - Chat ID
   * @param muted - Mute status
   */
  async mute(tel: string, chatId: string, muted: boolean = true): Promise<{ success: boolean }> {
    return this.client['patch']<{ success: boolean }>(
      `/api/chats/${encodeURIComponent(chatId)}`,
      { tel, muted }
    );
  }

  /**
   * Clear chat history
   * @param tel - Phone number
   * @param chatId - Chat ID
   */
  async clearHistory(tel: string, chatId: string): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(
      `/api/chats/${encodeURIComponent(chatId)}/clear`,
      { tel }
    );
  }

  /**
   * Update multiple chat properties at once
   * @param tel - Phone number
   * @param chatId - Chat ID
   * @param updates - Properties to update
   */
  async update(
    tel: string,
    chatId: string,
    updates: {
      archived?: boolean;
      pinned?: boolean;
      muted?: boolean;
    }
  ): Promise<{ success: boolean }> {
    return this.client['patch']<{ success: boolean }>(
      `/api/chats/${encodeURIComponent(chatId)}`,
      { tel, ...updates }
    );
  }

  /**
   * Get group chats only
   * @param tel - Phone number
   * @param params - Additional query parameters
   */
  async getGroups(tel: string, params?: Omit<ChatFilters, 'tel' | 'groups'>): Promise<ChatsResponse> {
    return this.list({ tel, groups: true, ...params });
  }

  /**
   * Get individual chats only
   * @param tel - Phone number
   * @param params - Additional query parameters
   */
  async getIndividual(tel: string, params?: Omit<ChatFilters, 'tel' | 'groups'>): Promise<ChatsResponse> {
    return this.list({ tel, groups: false, ...params });
  }

  /**
   * Get unread chats only
   * @param tel - Phone number
   * @param params - Additional query parameters
   */
  async getUnread(tel: string, params?: Omit<ChatFilters, 'tel' | 'unreadOnly'>): Promise<ChatsResponse> {
    return this.list({ tel, unreadOnly: true, ...params });
  }

  /**
   * Get archived chats
   * @param tel - Phone number
   * @param params - Additional query parameters
   */
  async getArchived(tel: string, params?: Omit<ChatFilters, 'tel' | 'archived'>): Promise<ChatsResponse> {
    return this.list({ tel, archived: true, ...params });
  }

  /**
   * Search chats by name or content
   * @param tel - Phone number
   * @param query - Search query
   * @param params - Additional query parameters
   */
  async search(tel: string, query: string, params?: Omit<ChatFilters, 'tel' | 'search'>): Promise<ChatsResponse> {
    return this.list({ tel, search: query, ...params });
  }
}